import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { Pets } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SocialLoginButtons from '../components/SocialLoginButtons';
import { isHandlerSite, OWNER_SITE_URL, HANDLER_SITE_URL } from '../config/siteMode';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const AuthPage = () => {
  const navigate = useNavigate();
  const { login, socialLogin, signup, loading, error, isAuthenticated, user } = useAuth();

  // The ONE place a signed-in user is routed away from /auth — after login,
  // after signup, after social sign-in, or on arriving here already signed in
  // (bookmark, "Get Started" while logged in). The submit handlers used to
  // navigate too, racing this effect; on the handler site the loser of that
  // race decided whether a fresh signup saw the application flow or a
  // dashboard redirect chain.
  //
  // Owner site: staff to the admin portal, everyone else to the role router.
  // Handler site: approved handlers to the role router (which owns the
  // handler-dashboard decision); everyone else straight into the application
  // flow — there are no owner/admin surfaces on this build to bounce through.
  //
  // Waits out `loading` so a hard refresh doesn't redirect before the stored
  // session has been verified.
  useEffect(() => {
    if (loading || !isAuthenticated) return;
    if (isHandlerSite) {
      navigate(user?.is_handler ? '/dashboard' : '/handler/apply', { replace: true });
    } else {
      navigate(user?.is_staff ? '/admin/dashboard' : '/dashboard', { replace: true });
    }
  }, [loading, isAuthenticated, user, navigate]);

  const handleSocialAuth = async (provider, token) => {
    setLocalError('');
    const result = await socialLogin(provider, token);
    if (!result.success) {
      setLocalError(result.error || `${provider} sign-in failed`);
    }
    // Success: the signed-in effect above places the user.
  };

  // Which tab greets a visitor is site-specific. The handler site's landing
  // redirects straight here as its "become a handler" entry point, so it must
  // open on Sign Up: opening on Login (the owner-site default) put a login
  // form under the "Sign up to become a verified pet handler" headline — new
  // applicants submitted their details to login (and saved owner credentials
  // autofilled and signed people in) from what read as the signup page.
  const [tabValue, setTabValue] = useState(isHandlerSite ? 1 : 0);
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    userType: 'owner',
  });
  const [signupData, setSignupData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    userType: 'owner',
  });
  const [localError, setLocalError] = useState('');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setLocalError('');
  };

  const handleLoginChange = (field) => (event) => {
    setLoginData({ ...loginData, [field]: event.target.value });
    setLocalError('');
  };

  const handleSignupChange = (field) => (event) => {
    setSignupData({ ...signupData, [field]: event.target.value });
    setLocalError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!loginData.email || !loginData.password) {
      setLocalError('Please fill in all fields');
      return;
    }

    const result = await login(loginData.email, loginData.password);

    if (!result.success) {
      setLocalError(result.error || 'Login failed');
    }
    // Success: the signed-in effect places the user (site- and role-aware).
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!signupData.firstName || !signupData.lastName || !signupData.email || !signupData.password || !signupData.confirmPassword) {
      setLocalError('Please fill in all fields');
      return;
    }

    if (signupData.password !== signupData.confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (signupData.password.length < 6) {
      setLocalError('Password must be at least 6 characters');
      return;
    }

    // Owner site => owner account. Handler site => a normal account that then
    // goes through the handler application/approval flow (never is_handler at
    // signup, or they'd skip approval).
    const userType = 'owner';
    const result = await signup(signupData.firstName, signupData.lastName, signupData.email, signupData.password, signupData.confirmPassword, userType);

    if (!result.success) {
      setLocalError(result.error || 'Signup failed');
    }
    // Success: the signed-in effect places the user — on the handler site
    // that is the application flow, on the owner site the role router.
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, md: 8 } }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Pets sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {isHandlerSite ? 'PetTrek for Handlers' : 'Welcome to PetTrek'}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {isHandlerSite
            ? 'Sign up to become a verified pet handler and start earning'
            : 'Join our community of dog lovers'}
        </Typography>
      </Box>

      <Paper elevation={8} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              py: 2,
            },
          }}
        >
          <Tab label="Login" />
          <Tab label="Sign Up" />
        </Tabs>

        <Box sx={{ p: 4 }}>
          {(error || localError) && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {localError || error}
            </Alert>
          )}

          {/* Login Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box component="form" onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={loginData.email}
                onChange={handleLoginChange('email')}
                margin="normal"
                required
                autoComplete="email"
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={loginData.password}
                onChange={handleLoginChange('password')}
                margin="normal"
                required
                autoComplete="current-password"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 3,
                  py: 1.5,
                  // Auth runs in the 60% register: keylines, no offset shadows.
                  boxShadow: 'none', '&:active': { boxShadow: 'none', transform: 'none' },
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Login'}
              </Button>

              {/* The reset flow existed on the backend but had no entry point
                  anywhere in the UI — a locked-out user had no way in. */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link component={RouterLink} to="/forgot-password" variant="body2">
                  Forgot your password?
                </Link>
              </Box>
            </Box>
            <SocialLoginButtons onAuth={handleSocialAuth} disabled={loading} />
          </TabPanel>

          {/* Signup Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box component="form" onSubmit={handleSignup}>
              <TextField
                fullWidth
                label="First Name"
                value={signupData.firstName}
                onChange={handleSignupChange('firstName')}
                margin="normal"
                required
                autoComplete="given-name"
              />
              <TextField
                fullWidth
                label="Last Name"
                value={signupData.lastName}
                onChange={handleSignupChange('lastName')}
                margin="normal"
                required
                autoComplete="family-name"
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={signupData.email}
                onChange={handleSignupChange('email')}
                margin="normal"
                required
                autoComplete="email"
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={signupData.password}
                onChange={handleSignupChange('password')}
                margin="normal"
                required
                autoComplete="new-password"
                helperText="Must be at least 6 characters"
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={signupData.confirmPassword}
                onChange={handleSignupChange('confirmPassword')}
                margin="normal"
                required
                autoComplete="new-password"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 3,
                  py: 1.5,
                  // Auth runs in the 60% register: keylines, no offset shadows.
                  boxShadow: 'none', '&:active': { boxShadow: 'none', transform: 'none' },
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Create Account'}
              </Button>
            </Box>
            <SocialLoginButtons onAuth={handleSocialAuth} disabled={loading} />
          </TabPanel>
        </Box>
      </Paper>

      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {isHandlerSite ? (
            <>
              Looking to book a pet handler?{' '}
              <Link href={OWNER_SITE_URL} underline="hover">
                Go to PetTrek
              </Link>
            </>
          ) : (
            <>
              Want to earn as a pet handler?{' '}
              <Link href={HANDLER_SITE_URL} underline="hover">
                Apply here
              </Link>
            </>
          )}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </Typography>
      </Box>
    </Container>
  );
};

export default AuthPage; 